import apiClient from './client';

// Scenario Definition interfaces
export interface ScenarioDefinition {
  id: number;
  name: string;
  type: string;
  description: string;
  default_percentage_adjustment?: number;
  default_temporal_distribution?: YearlyPercentage[];
  default_temporal_years?: number;
  default_temporal_type?: string;
  display_order: number;
  color_code: string;
  icon_name: string;
  is_active: boolean;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

export interface UpdateScenarioDefinitionRequest {
  default_percentage_adjustment?: number;
}

export interface MetricScenario {
  id: number;
  metric_id: string;
  metric_name: string;
  cluster_code: string;
  cluster_name: string;
  sub_dimension_name: string;
  country_id: number;
  scenario_type: string;
  percentage_change?: number;
  absolute_value?: number;
  calculation_method?: string;
  description?: string;
  effective_date?: string;
  end_date?: string;
  is_active: boolean;
  created_by?: string;
  updated_by?: string;
  created_at: string;
  updated_at: string;
}

export interface UpdateMetricScenarioRequest {
  percentage_change?: number;
  absolute_value?: number;
}

// Temporal Distribution interfaces
export interface YearlyPercentage {
  year: number;
  percentage: number;
}

export interface TemporalDistribution {
  id: number;
  country_id: number;
  yearly_percentages: YearlyPercentage[];
  template_name?: string;
  is_active: boolean;
  created_by?: string;
  updated_by?: string;
  created_at: string;
  updated_at: string;
}

export interface TemporalDistributionResponse extends TemporalDistribution {
  country_name: string;
}

export interface CreateTemporalDistributionRequest {
  country_id: number;
  yearly_percentages: YearlyPercentage[];
  template_name?: string;
}

export interface UpdateTemporalDistributionRequest {
  yearly_percentages?: YearlyPercentage[];
  template_name?: string;
  is_active?: boolean;
}

export interface DefaultTemporalDistribution {
  id: number;
  yearly_percentages: YearlyPercentage[];
  temporal_years: number;
  temporal_type: string;
  updated_by?: string;
  updated_at: string;
}

export interface DefaultTemporalDistributionRequest {
  yearly_percentages: YearlyPercentage[];
  temporal_type: string;
}

export interface EffectiveTemporalDistribution {
  country_id: number;
  country_name: string;
  scenario_definition_id: number;
  scenario_name: string;
  scenario_type: string;
  distribution_source: 'country-specific' | 'default';
  yearly_percentages: YearlyPercentage[];
  distribution_type: string;
}

const scenarioApi = {
  // Get all scenario definitions
  async getScenarioDefinitions(activeOnly: boolean = false): Promise<ScenarioDefinition[]> {
    try {
      const response = await apiClient.getRaw<ScenarioDefinition[]>('/scenarios/definitions', {
        params: { active_only: activeOnly }
      });
      return response.data || [];
    } catch (error) {
      console.error('Error fetching scenario definitions:', error);
      throw error;
    }
  },

  // Update a scenario definition
  async updateScenarioDefinition(id: number, data: UpdateScenarioDefinitionRequest): Promise<ScenarioDefinition> {
    try {
      return await apiClient.put(`/scenarios/definitions/${id}`, data);
    } catch (error) {
      console.error('Error updating scenario definition:', error);
      throw error;
    }
  },

  // Get metric scenarios by country
  async getMetricScenariosByCountry(countryId: number): Promise<MetricScenario[]> {
    try {
      const response = await apiClient.getRaw<MetricScenario[]>(`/scenarios/metrics/country/${countryId}`);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching metric scenarios for country:', error);
      throw error;
    }
  },

  // Update a metric scenario
  async updateMetricScenario(id: number, data: UpdateMetricScenarioRequest): Promise<MetricScenario> {
    try {
      return await apiClient.put(`/scenarios/metrics/${id}`, data);
    } catch (error) {
      console.error('Error updating metric scenario:', error);
      throw error;
    }
  },

  // Temporal Distribution methods
  
  // Get temporal distribution for a country
  async getTemporalDistribution(countryId: number): Promise<TemporalDistributionResponse | null> {
    try {
      const response = await apiClient.getRaw<TemporalDistributionResponse>(`/scenarios/temporal/country/${countryId}`);
      return response.data || null;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      console.error('Error fetching temporal distribution:', error);
      throw error;
    }
  },

  // Create or update a temporal distribution
  async createOrUpdateTemporalDistribution(data: CreateTemporalDistributionRequest): Promise<TemporalDistribution> {
    try {
      return await apiClient.post('/scenarios/temporal', data);
    } catch (error) {
      console.error('Error creating/updating temporal distribution:', error);
      throw error;
    }
  },

  // Update a temporal distribution by ID
  async updateTemporalDistribution(id: number, data: UpdateTemporalDistributionRequest): Promise<TemporalDistribution> {
    try {
      return await apiClient.put(`/scenarios/temporal/${id}`, data);
    } catch (error) {
      console.error('Error updating temporal distribution:', error);
      throw error;
    }
  },

  // Delete a temporal distribution
  async deleteTemporalDistribution(id: number): Promise<void> {
    try {
      await apiClient.delete(`/scenarios/temporal/${id}`);
    } catch (error) {
      console.error('Error deleting temporal distribution:', error);
      throw error;
    }
  },

  // Default temporal distribution methods
  
  // Get the single default temporal distribution that applies to all scenarios
  async getDefaultTemporalDistribution(): Promise<DefaultTemporalDistribution> {
    try {
      const response = await apiClient.getRaw<DefaultTemporalDistribution>('/scenarios/temporal/default');
      return response.data;
    } catch (error) {
      console.error('Error fetching default temporal distribution:', error);
      throw error;
    }
  },

  // Update the single default temporal distribution that applies to all scenarios
  async updateDefaultTemporalDistribution(data: DefaultTemporalDistributionRequest): Promise<void> {
    try {
      await apiClient.put('/scenarios/temporal/default', data);
    } catch (error) {
      console.error('Error updating default temporal distribution:', error);
      throw error;
    }
  },

  // Get effective temporal distribution for a country/scenario combination
  async getEffectiveTemporalDistribution(countryId: number, scenarioType: string): Promise<EffectiveTemporalDistribution> {
    try {
      const response = await apiClient.getRaw<EffectiveTemporalDistribution>('/scenarios/temporal/effective', {
        params: { countryId, scenarioType }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching effective temporal distribution:', error);
      throw error;
    }
  }
};

export default scenarioApi;